import type { NavigatorCategory, NavigatorProvider, NavigatorResultsViewModel } from './navigator-results.types';

export type BestMatchEntry = {
  provider: NavigatorProvider;
  category: NavigatorCategory;
};

/**
 * Feature 087 (ticket 15) — finds the plan-wide Best Match already computed
 * server-side (`provider.isBestMatch`, additive on the live find-providers
 * contract). The backend guarantees at most one distinct provider id is
 * marked, but the same provider can be flagged in more than one specialty
 * bucket. Rank flagged occurrences with the same tie-breaks as the backend's
 * selector (score DESC, primary specialty, existing specialty/category
 * order, name, id) so the highlight always shows the winning category
 * context, not just whichever bucket happens to render first.
 */
export const findBestMatchEntry = (
  viewModel: NavigatorResultsViewModel,
): BestMatchEntry | undefined => {
  const entries: BestMatchEntry[] = [];
  viewModel.categories.forEach((category) => {
    category.providers.forEach((provider) => {
      if (provider.isBestMatch) entries.push({ provider, category });
    });
  });
  if (entries.length === 0) return undefined;

  const categoryOrder = new Map(viewModel.categories.map((category, index) => [category.id, index]));

  entries.sort(
    (a, b) =>
      (b.provider.score ?? -Infinity) - (a.provider.score ?? -Infinity) ||
      Number(b.category.isPrimary) - Number(a.category.isPrimary) ||
      (categoryOrder.get(a.category.id)! - categoryOrder.get(b.category.id)!) ||
      a.provider.name.localeCompare(b.provider.name) ||
      a.provider.id.localeCompare(b.provider.id),
  );

  return entries[0];
};
